/**
 * Strings of screen share (Go Live) and camera video in voice
 * (docs/decisions/2026-10-29-screen-share-and-video.md §4.7). English only.
 */
import type { Dict } from './core';

export const streamEn: Dict = {
  // the control bar and the dock
  'stream.share': 'Share your screen',
  'stream.stop': 'Stop streaming',
  'stream.camOn': 'Turn on camera',
  'stream.camOff': 'Turn off camera',
  'stream.soon': 'Coming soon',
  'stream.soonBody': 'Video calls are on their way.',
  'stream.noPerm': "You don't have permission to stream in this channel.",
  // the share sheet
  'stream.sheet.title': 'Share your screen',
  'stream.sheet.res': 'Resolution',
  'stream.sheet.fps': 'Frame rate',
  'stream.sheet.sound': 'Share sound',
  'stream.sheet.echo': 'People may hear the call through your stream',
  'stream.sheet.go': 'Go Live',
  'stream.res': '{n}p',
  'stream.fpsN': '{n} fps',
  // tiles
  'stream.live': 'Live',
  'stream.liveLabel': 'Streaming',
  'stream.camLabel': 'Camera on',
  'stream.watch': 'Watch stream',
  'stream.watchJoin': 'Join and watch',
  'stream.stopWatching': 'Stop watching',
  'stream.of': "{name}'s stream",
  'stream.yours': 'Your stream',
  'stream.paused': 'Nobody is watching',
  'stream.viewers': { one: '{n} watching', other: '{n} watching' },
  // the last item of "Ana, Bahar and 3 more" (the list adds the "and")
  'stream.viewersMore': '{n} more',
  'stream.noViewers': 'Nobody is watching yet',
  'stream.sound': 'Stream sound',
  'stream.soundMuted': 'Stream sound muted',
  'stream.volume': 'Stream volume',
  'stream.muteSound': 'Mute stream sound',
  'stream.unmuteSound': 'Unmute stream sound',
  'stream.noSound': 'No stream sound',
  'stream.noSoundBody':
    '{name} is sharing without sound. They can turn on Share sound when they go live.',
  'stream.menu': 'Stream options',
  'stream.quality': 'Stream quality',
  // the pop-out: a stream you watch while its voice room is away
  'stream.mini.back': "Back to {name}'s stream",
  'stream.mini.backTip': 'Back to the stream',
  'stream.mini.fold': 'Minimize',
  'stream.mini.show': 'Show stream',
  'stream.focus': 'Focus',
  'stream.grid': 'Back to grid',
  'stream.fullscreen': 'Full screen',
  'stream.exitFullscreen': 'Exit full screen',
  'stream.liveNow': '{name} is live',
  'stream.loading': 'Starting stream',
  // errors (toasts)
  'stream.err.limit': {
    one: 'This channel already has {n} live stream',
    other: 'This channel already has {n} live streams',
  },
  'stream.err.camLimit': {
    one: 'This channel already has {n} camera on',
    other: 'This channel already has {n} cameras on',
  },
  'stream.err.overLimit': "Your stream was stopped: it went over this server's quality limit.",
  'stream.err.camOverLimit':
    "Your camera was turned off: it went over this server's quality limit.",
  'stream.err.noStream': 'That stream has ended.',
  'stream.err.soon': 'Video is coming soon.',
  'stream.err.off': 'Screen sharing is turned off on this server.',
  'stream.err.old': "Screen sharing isn't available on this voice server yet.",
  'stream.err.unsupported': "Screen sharing isn't available on this connection.",
  'stream.err.camUnsupported': "Video isn't available on this connection.",
  'stream.err.noMedia': "Your stream couldn't start. Try again.",
  'stream.err.camNoMedia': "Your camera couldn't start. Try again.",
  'stream.err.staff': 'Neosocial staff stopped your stream.',
  'stream.err.camStaff': 'Neosocial staff turned off your camera.',
  'stream.err.capture': "Couldn't start sharing your screen.",
  'stream.err.camera': "Couldn't turn on your camera. Check that no other app is using it.",
  'stream.err.camBlocked': 'Camera access is blocked. Allow it in your browser settings.',
  'stream.err.unknown': 'Something went wrong. Try again.',
  // Settings › Voice & audio: the camera
  'stream.set.camera': 'Camera',
  'stream.set.cameraHint': 'Used when you turn on your camera in a voice channel.',
  'stream.set.default': 'Default camera',
  'stream.set.soon': 'Video in voice channels is coming soon.',
  'stream.set.test': 'Test camera',
  'stream.set.stopTest': 'Stop test',
  // the permissions editor (Discord's "Video", the STREAM bit)
  'ns.perm.STREAM': 'Video',
  'ns.permd.STREAM': 'Share your screen or turn on your camera in voice channels.',
  // the voice room's stage, header and pop-out, like Discord's (voice-room audit P2)
  'hubv.vr.videoOff': 'Disable video',
  'hubv.vr.videoOn': 'Enable video',
  'hubv.vr.videoIsOff': 'You disabled their video',
  'hubv.vr.showNonVideo': 'Show non-video participants',
  'hubv.vr.hideNonVideo': 'Hide non-video participants',
  'hubv.vr.stripHide': 'Hide participants',
  'hubv.vr.stripShow': 'Show participants',
  'hubv.vr.solo': "It's just you here",
  'hubv.vr.soloInvite': 'Invite friends to this voice channel.',
  'hubv.vr.soloWait': 'When someone joins, they show up here.',
  'hubv.vr.copyInvite': 'Copy invite',
  'hubv.vr.inviteCopied': 'Invite link copied',
  'stream.mini.pip': 'Picture in picture',
  'stream.mini.pipExit': 'Exit picture in picture',
  'stream.mini.pipOn': 'Playing in picture in picture',
};
